// The agent portal.
//
// A different audience from everything else in this app: an agent signs in
// with a link emailed to the address on their matters, sees those matters and
// nothing else, and downloads the contract once staff have released it.
//
// ── Why this is its own file ─────────────────────────────────────────────────
//
// App.js is the staff portal and is deliberately one file. This is not more of
// it: it is a separate surface for people outside the firm, and the one thing
// worth guaranteeing about it is that **nothing internal can reach it by
// accident**. Sharing a component tree with the screen that renders `notes`,
// `compileError` and the vendor's Form 27 answers is how something internal
// gets rendered here by a prop nobody checked. They share `shared.js` — the
// brand colours, the service URL, the firm's wordmark and the one date format
// both screens print — and nothing else.
//
// ── Scoping is not this file's job, and must never become it ─────────────────
//
// Every read below is already scoped by the middleware off the session. There
// is no filtering here for *access*, and there must not be: `?fh=` on the staff
// dashboard is the cautionary tale — it narrows the screen while the API keeps
// returning everything, which is fine for staff in their own lane and a data
// breach applied to an external audience. If this file ever appears to need a
// filter to decide who sees what, the endpoint is wrong. The sidebar below
// *does* filter, and the distinction is the whole of why it is safe: it splits
// one already-scoped list into two views of itself.
//
// ── The layout is the mockup's, and it is a shell rather than a column ───────
//
// It was a 720px centred column: a text wordmark, a heading, and a stack of
// cards. That reads as one page rather than as a place, and it gave an agent
// nowhere to stand — every matter, released or not, in one undifferentiated
// run, with the thing they came for (the contract) indistinguishable from the
// thing they are waiting on.
//
// So: a 56px bar carrying the firm's own mark, a 208px rail that says what
// this holds, and a table-shaped list. **Classes rather than inline styles**,
// which is the one place this file departs from App.js's convention and it is
// not a preference: hover, `aria-current` and a breakpoint cannot be written
// inline, and the alternative is a style object per state. The palette is
// still `shared.js`'s and is interpolated into the sheet, so there is no
// second copy of a colour.

import { useState, useEffect, useCallback, useRef } from "react";
import { T, MIDDLEWARE_URL, LOGO_SRC, formatOpenedDate } from "./shared";

const SESSION_KEY = "s32_agent_session";

// Whether the rail is folded. A per-viewer preference and nothing else — the
// service never sees it, and losing it costs one click.
const RAIL_KEY = "s32_agent_rail";

// Green means released and nothing else, the same rule the staff badges follow:
// *ready to download* is the only irreversible-feeling state on this screen,
// and giving *preparing* a colour of its own would make the one pair an agent
// must not confuse look alike.
const READY = "#2A8A52";

// ── The link token, read once at module load ─────────────────────────────────
//
// Read and **stripped from the URL immediately**, the same way App.js handles
// `?token=`, so it stays out of the address bar, bookmarks and screenshots.
//
// Reading it here rather than in an effect also settles a trap that would
// otherwise be very hard to see: the token is single-use, and React's
// StrictMode runs effects twice in development. An effect that read the URL
// each time would redeem the token, then redeem it again and be told the link
// had expired — on a sign-in that had just succeeded. One value, read once,
// with one in-flight request against it (see `redeeming` below).
function readLinkToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t") || "";
    if (!token) return "";
    const url = new URL(window.location.href);
    url.searchParams.delete("t");
    window.history.replaceState({}, "", url.toString());
    return token;
  } catch (e) {
    return "";
  }
}

const LINK_TOKEN = typeof window === "undefined" ? "" : readLinkToken();

// ── Where the agent is, in the address bar (DA-15) ──────────────────────────
//
// The view and the open matter were state alone, so the browser's Back left
// the portal entirely, a reload dropped the agent on the list, and a matter
// could not be bookmarked or pasted to a colleague. `?m=<ref>` is the open
// matter and `?v=` the view when it is not *Matters*. The address names a
// matter, never grants one: the endpoint still scopes it, and a ref that is
// not theirs answers as not-there. Read after `readLinkToken`, which strips
// only `?t=`, so a link that carries both keeps the matter.
export const AGENT_VIEWS = ["matters", "contracts", "order"];

export function readAgentLocation(search = "") {
  try {
    const params = new URLSearchParams(search);
    const v = params.get("v") || "";
    return {
      view: AGENT_VIEWS.includes(v) ? v : "matters",
      ref: params.get("m") || null,
    };
  } catch (e) {
    return { view: "matters", ref: null };
  }
}

export function agentLocationSearch({ view = "matters", ref = null } = {}) {
  const params = new URLSearchParams();
  if (view && view !== "matters") params.set("v", view);
  if (ref) params.set("m", ref);
  const q = params.toString();
  return q ? `?${q}` : "";
}

const writeAgentLocation = ({ view, ref }, how = "push") => {
  try {
    const url = new URL(window.location.href);
    url.search = agentLocationSearch({ view, ref });
    const state = { ap: ref ? "matter" : "list" };
    if (how === "replace") window.history.replaceState(state, "", url.toString());
    else window.history.pushState(state, "", url.toString());
  } catch (e) { /* the address is a convenience; the screen is already right */ }
};

// ── Finding one matter in a long list (DA-16) ───────────────────────────────
//
// Every word has to appear somewhere in the address, the vendor or the ref, in
// any order — "kew smith" finds 4 Main St Kew for Jo Smith. The list it narrows
// is the endpoint's, already scoped; this only hides rows, it never adds any.
export const MATTER_SEARCH_MIN = 8;

export function matterMatches(m = {}, q = "") {
  const words = String(q).toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [m.address, m.client, m.ref].filter(Boolean).join(" ").toLowerCase();
  return words.every(w => hay.includes(w));
}

// The redemption, shared between however many times the effect runs. A promise
// rather than a boolean, so a second caller waits for the first one's answer
// instead of racing past it into a signed-out state.
let redeeming = null;

const readStoredSession = () => {
  try { return localStorage.getItem(SESSION_KEY) || ""; } catch (e) { return ""; }
};
const writeStoredSession = (token) => {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch (e) { /* storage blocked — the cookie may still carry the session */ }
};

// ── Talking to the middleware ────────────────────────────────────────────────
//
// The session travels one of two ways and this sends both, which is what makes
// the move to cookie-only a configuration change rather than a rewrite:
//
//   - `Authorization: Bearer` from what `/agent/session` returned, which is
//     what works today across `*.web.app` → `*.run.app`.
//   - the HttpOnly cookie, which the browser sends **by itself once the two are
//     on one origin** — fetch's default credentials mode is `same-origin`, so
//     this is already correct and needs no flag.
//
// `credentials: "include"` is deliberately *not* set. It would be required to
// send the cookie cross-origin, and the middleware's `cors({origin: true})`
// does not send `Access-Control-Allow-Credentials` — so the browser would
// reject every response rather than falling back.
export function agentFetch(path, { token, ...options } = {}) {
  return fetch(`${MIDDLEWARE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

// What went wrong, in words an agent can act on.
//
// The middleware's own messages are written for staff and for logs — "the
// agent portal is not configured — AGENT_PORTAL_URL is unset" describes our
// infrastructure to somebody who only wants to sign in. So the codes are
// translated here and anything unrecognised gets a plain sentence rather than
// a raw field.
export function signInError(status, body) {
  if (status === 429) {
    return "Too many sign-in links have been requested for that address. Please try again in a few minutes.";
  }
  if (status === 503) {
    return "Signing in is unavailable at the moment. Please contact us and we will send your contract directly.";
  }
  if (status === 502) {
    return "The sign-in link could not be sent. Please try again, or contact us if it keeps happening.";
  }
  if (body?.code === "link_expired") {
    return "That sign-in link has already been used or has expired. Enter your email address for a new one.";
  }
  return "Something went wrong. Please try again.";
}

// A timeline entry's date. Events arrive as ISO 8601 — the middleware
// normalises them at that boundary precisely so this can be a one-liner — but
// an unparseable one renders as nothing rather than "Invalid Date".
export function eventDate(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

// Two letters for the avatar. The name where the directory has one, the
// address otherwise — an agent whose record carries no name still has to be
// able to tell whose session this is.
export function initialsOf(name, email) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return String(email || "?").trim().slice(0, 2).toUpperCase();
}

// ── Small pieces ─────────────────────────────────────────────────────────────

const Btn = ({ children, onClick, variant = "primary", disabled, type = "button", className = "" }) => (
  <button type={type} onClick={disabled ? undefined : onClick} disabled={disabled}
    className={`ap-btn ${variant} ${className}`.trim()}>
    {children}
  </button>
);

const Spinner = ({ size = 28 }) => (
  <div role="status" aria-label="Loading" style={{
    width: size, height: size, border: `3px solid ${T.border}`, borderTopColor: T.brown,
    borderRadius: "50%", animation: "s32-spin 0.8s linear infinite",
  }} />
);

const Centred = ({ children }) => (
  <div className="ap-centred">{children}</div>
);

const Notice = ({ children, tone = "info" }) => (
  <div role={tone === "error" ? "alert" : undefined}
    className={`ap-note${tone === "error" ? " ap-alert" : ""}`}>{children}</div>
);

// How far through the searches a matter is. Shown only once there is something
// to count — "0 of 0" on a matter whose searches have not been ordered yet
// reads as nothing happening rather than as nothing having started.
//
// **`compact` is the list's version and the words are the difference.** In a
// column under a heading reading *Searches*, "11 of 11 certificates back"
// repeats the heading and wraps to three lines; on the matter screen there is
// no heading above it, so the sentence is what says what is being counted.
export const Progress = ({ matter, compact = false }) => {
  const total = matter.searchCount || 0;
  if (!total) return null;
  const done = matter.returnedCount || 0;
  const pct = Math.round((done / total) * 100);
  const bar = (
    <div style={{
      flex: compact ? "1 1 auto" : "0 0 90px", height: 5, background: T.bgAlt,
      borderRadius: 999, overflow: "hidden",
    }}>
      <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? READY : T.brown, transition: "width 0.4s" }} />
    </div>
  );

  if (compact) {
    return (
      <div>
        {bar}
        <div style={{ fontSize: 12, color: T.textFaint, marginTop: 5, fontVariantNumeric: "tabular-nums" }}>
          {done} of {total}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {bar}
      <span style={{ fontSize: 12, color: T.textFaint }}>{done} of {total} certificates back</span>
    </div>
  );
};

// The stage, printed as the middleware worded it.
//
// **The wording is not this file's**, for the same reason the timeline's is
// not: two copies would drift, and staff and agent would then be reading
// different accounts of the same matter. The label comes down with the matter;
// all this decides is what it looks like.
//
// **`matter.contract`, not `matter.stage`.** The projection nests *the file*
// and *the contract* separately, because each has a stage of its own and
// flattening them puts two unrelated ones in the same shape. This screen shows
// the contract's — it is the thing an agent is waiting for — and `file.status`
// is what the row below it reads.
//
// **An outline rather than a tint**, the rule the staff badges follow: five
// filled pills are five colours competing at one weight, and the only thing
// that has to stand out here is *ready*.
export const Stage = ({ matter }) => {
  const contract = matter.contract || {};
  const ready = contract.stage === "released";
  return (
    <span className="ap-pill" style={{ color: ready ? READY : T.textFaint }}>
      {contract.label}
    </span>
  );
};

// ── What each view holds ─────────────────────────────────────────────────────
//
// **Matters is the whole list and filters nothing.** It is every file this
// agent is acting on with us, purchase or sale, and a matter stays in it after
// exchange — a sold file is still the agent's transaction, and taking it off
// the screen the day it exchanges would hide the half they are chasing hardest.
// There is deliberately no rule here to write: the endpoint has already scoped
// the list, and anything this file removed from it would be removed from the
// only place an agent can see it.
//
// **Whose matters that is, is the endpoint's answer and not this file's.** An
// ordinary agent is granted their own; an agent staff have made an office admin
// is granted the agency's. `officeAdmin` on the session is sent so the heading
// can say which of the two somebody is reading — a list that says *this agency*
// to a person seeing only their own listings is a list they will read as
// missing rows.
//
// **Contracts is the live window: released, and not yet exchanged.** It was
// every released matter, so a contract that exchanged months ago sat in it
// beside one going out this morning — which turns the view an agent opens to
// answer *what am I waiting on* into an archive that grows for ever.
//
// **`!== "sold"` rather than `=== "pre-exchange"`, and that is the whole of the
// rule.** `file.position` is the middleware's — `pre-exchange`, `sold`, or
// `null` where it cannot tell — and `null` is the *ordinary* case rather than
// the exception: the office's stage only reaches a matter through a direct
// Form 265 post, which fires on entry update, so most live matters carry none.
// Requiring a positive pre-exchange answer would empty this view on nearly
// every matter. Asking the negative shows everything except what is known to
// have exchanged, which is the same direction of error the middleware takes in
// reporting a file `open` where it cannot tell.
//
// It is also what makes this safe against a middleware that predates the field:
// `undefined !== "sold"` is true, so the view falls back to every released
// matter — today's behaviour — rather than to none.
//
// **Never re-derive the position from `file.stage`.** The office's own stage
// names live in `lib/agent-stage.js`; a copy of them in this bundle goes stale
// the day one is renamed, silently, on this screen and not the staff one.
export const inContracts = (matter = {}) =>
  Boolean(matter.contract?.available)
  && !matter.settlementDate
  && matter.file?.position !== "sold"
  && matter.file?.side !== "purchase";

// ── The rail's two icons, and the one that folds it ──────────────────────────
//
// Inline SVG at `strokeWidth="2"`, the convention App.js follows — no icon
// dependency for three glyphs. They exist because a folded rail has nowhere to
// put a word: a 56px strip of blank buttons is a worse control than none.
const Icon = ({ children, ...rest }) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>
    {children}
  </svg>
);

const IconMatters = () => (
  <Icon><path d="M3 7h18M3 12h18M3 17h12" /></Icon>
);

const IconContracts = () => (
  <Icon>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </Icon>
);

// A page with a plus in it, deliberately not a page with lines: `IconContracts`
// is a document that exists and this is one being asked for, and two glyphs a
// folded rail cannot tell apart are two buttons nobody can tell apart.
const IconOrder = () => (
  <Icon>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M12 12v5M9.5 14.5h5" />
  </Icon>
);

const IconFold = ({ open }) => (
  <Icon>{open ? <path d="M15 6l-6 6 6 6" /> : <path d="M9 6l6 6-6 6" />}</Icon>
);

// ── Signing in ───────────────────────────────────────────────────────────────

export function SignIn({ initialError }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState(initialError || "");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Please enter the email address we have for you.");
      return;
    }
    setState("sending");
    setError("");
    try {
      const res = await agentFetch("/agent/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        setError(signInError(res.status, await res.json().catch(() => null)));
        setState("idle");
        return;
      }
      setState("sent");
    } catch (err) {
      setError("Could not reach us just now. Please check your connection and try again.");
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <Centred>
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <h1 className="ap-h1">Check your inbox</h1>
          {/* The middleware answers identically whether or not the address is
              known to us — that is what stops this being a way to ask which
              agents the firm acts for. So this cannot say "sent", and the copy
              has to carry the "if" rather than promise something that may not
              have happened. */}
          <p className="ap-lede" style={{ marginTop: 14 }}>
            If <strong style={{ color: T.text }}>{email}</strong> is the address on a matter with us,
            a sign-in link is on its way.
          </p>
          <p style={{ fontSize: 13, color: T.textFaint, lineHeight: 1.7 }}>
            The link works once and expires in 15 minutes. If it does not arrive, check your
            junk folder, or ask us to send your contract directly.
          </p>
          <Btn variant="raised" onClick={() => setState("idle")}>Use a different address</Btn>
        </div>
      </Centred>
    );
  }

  return (
    <Centred>
      <form onSubmit={submit} style={{ width: "100%", maxWidth: 400 }}>
        <h1 className="ap-h1">Sign in</h1>
        <p className="ap-lede" style={{ margin: "14px 0 20px" }}>
          Enter the email address we hold for you and we will send a sign-in link.
          There is no password to remember.
        </p>

        {error && <div style={{ marginBottom: 16 }}><Notice tone="error">{error}</Notice></div>}

        <label htmlFor="agent-email" className="ap-label">Email address</label>
        <input id="agent-email" type="email" value={email} autoComplete="email" autoFocus
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@agency.com.au" className="ap-input" />

        <Btn type="submit" disabled={state === "sending"} className="ap-wide">
          {state === "sending" ? "Sending…" : "Send me a sign-in link"}
        </Btn>
      </form>
    </Centred>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────
//
// The mockup's table shape rather than a stack of cards: five columns, because
// what an agent is comparing across matters is where each one has got to, and
// a card each buries that in prose. The header row is what gives the cells
// their nouns — which is why the progress cell here says "11 of 11" and the
// matter screen's says "11 of 11 certificates back".
//
// **The row is a button.** It is the whole target, so a keyboard reaches it as
// one thing rather than as five, and the cells inside carry `data-label` for
// the phone, where the header is gone and a bare date reads as nothing.

const COLUMNS = ["Property", "Vendor", "Opened", "Searches", "Contract"];

export function MatterList({ matters, onOpen, empty }) {
  if (!matters.length) {
    return (
      <div style={{ padding: "48px 0", maxWidth: 460 }}>
        <h2 className="ap-h2" style={{ marginBottom: 10 }}>{empty?.title || "Nothing here yet"}</h2>
        <p className="ap-lede">
          {empty?.body || "Matters appear here once we have opened a file. If you are expecting one, please get in touch."}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="ap-listhead" aria-hidden="true">
        {COLUMNS.map((c) => <div key={c}>{c}</div>)}
      </div>
      {matters.map((m) => (
        <button key={m.ref} onClick={() => onOpen(m.ref)} className="ap-row">
          <div className="ap-propcell">
            <div className="ap-prop">{m.address || m.ref}</div>
            <div className="ap-ref">{m.ref}</div>
          </div>
          <div className="ap-cell" data-label="Vendor">{m.client || "—"}</div>
          <div className="ap-cell" data-label="Opened">{formatOpenedDate(m.openedDate)}</div>
          <div className="ap-cell" data-label="Searches"><Progress matter={m} compact /></div>
          <div className="ap-stage"><Stage matter={m} /></div>
        </button>
      ))}
    </div>
  );
}

// ── One matter ───────────────────────────────────────────────────────────────

export function Timeline({ events }) {
  if (!events?.length) {
    // Events cannot be backfilled, so a matter opened before the timeline
    // existed genuinely has none. Saying so beats an empty panel that reads as
    // a page that failed to load.
    return <p className="ap-lede">There is no recorded history for this matter yet.</p>;
  }

  return (
    <ol className="ap-feed">
      {events.map((e, i) => (
        <li key={e.id || i}>
          <time>{eventDate(e.at)}</time>
          <div>
            {/* The line is the middleware's wording, rendered and not
                rewritten — the timeline's whole value is that a line can only
                appear because something recorded it. */}
            {e.line}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── What an agent does with the contract ─────────────────────────────────────
//
// **Open takes them to the page their release email opens**, and that is what
// changed. This panel used to hand over the PDF and nothing else, while the
// page the middleware builds for exactly this audience — the viewer that
// paginates on a phone where a framed PDF renders one sheet and stops, the
// summary of what the contract says, the press that asks the vendors to sign —
// was reachable only from an email. Two doors onto one document, and the
// portal had the poorer one.
//
// **The URL is asked for rather than assembled here.** It carries the matter's
// own release token, which is the middleware's to reuse or mint; a portal that
// built one would need that token in the bundle, and the session it *does*
// hold cannot travel in a navigation — an `<a href>` carries no header.
//
// **The download stays, as the quiet second action.** An agent who wants the
// file wants it now rather than after a page has drawn thirty-five sheets —
// and it is what still works against a middleware that has not deployed the
// route yet, which is the only sort of fallback a missing *route* allows.
export function Contract({ matter, token, onSignedOut }) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const contract = matter.contract || {};

  if (!contract.available) {
    return (
      <Notice>
        Your contract will be available here as soon as it has been checked and
        issued. We will email you when it is.
      </Notice>
    );
  }

  // A 401 from either press is a session that has gone, and is the one failure
  // that is not about the press. Everything else leaves the other action alone:
  // a page that would not open is a reason to offer the file, not to take it
  // away too.
  const ask = async (path, options) => {
    const res = await agentFetch(path, { token, ...options });
    if (res.status === 401) { onSignedOut(); return null; }
    return res;
  };

  const open = async () => {
    setState("opening");
    setError("");
    try {
      const res = await ask(`/agent/matters/${encodeURIComponent(matter.ref)}/view-link`, { method: "POST" });
      if (!res) return;
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.url) {
        setError(body?.code === "not_released"
          ? "This contract has not been issued yet."
          : "That contract could not be opened. You can still download it below.");
        setState("idle");
        return;
      }
      // **A navigation, not a new tab.** The URL is only known after a round
      // trip, and a `window.open` that far from the press is one a popup
      // blocker stops — which reads as a button that does nothing. The
      // browser's own Back is the way home.
      window.location.assign(body.url);
    } catch (err) {
      setError("Could not reach us just now. Please check your connection and try again.");
      setState("idle");
    }
  };

  // Fetched rather than linked, because the session travels in a header and an
  // <a href> cannot carry one. Putting the session token in a URL instead would
  // undo the reason it is not in one — it would land in history, in a shared
  // screenshot, and in whatever the browser syncs.
  //
  // Once the Hosting rewrite makes this same-origin the cookie goes with a
  // plain link and this can become an anchor.
  const download = async () => {
    setState("downloading");
    setError("");
    try {
      const res = await ask(`/agent/matters/${encodeURIComponent(matter.ref)}/contract`, {});
      if (!res) return;
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.code === "not_released"
          ? "This contract has not been issued yet."
          : "That contract could not be opened. Please contact us and we will send it to you.");
        setState("idle");
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Contract_and_S32_${matter.ref}_v${contract.version}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setState("idle");
    } catch (err) {
      setError("Could not reach us just now. Please check your connection and try again.");
      setState("idle");
    }
  };

  return (
    <div>
      <div className="ap-actions">
        <Btn onClick={open} disabled={state !== "idle"}>
          {state === "opening" ? "Opening…" : "Open the contract"}
        </Btn>
        <Btn variant="raised" onClick={download} disabled={state !== "idle"}>
          {state === "downloading" ? "Preparing…" : "Download the PDF"}
        </Btn>
      </div>
      {error && <div style={{ marginTop: 12 }}><Notice tone="error">{error}</Notice></div>}
    </div>
  );
}

// A value, or a stated absence. A blank cell in a list of facts about somebody
// else's matter reads as a page that failed to load; "Not recorded" reads as
// the answer it is.
const Fact = ({ label, value }) => (
  <>
    <dt>{label}</dt>
    <dd className={value ? "" : "blank"}>{value || "Not recorded"}</dd>
  </>
);

function MatterView({ matterRef, token, onBack, onSignedOut }) {
  const [matter, setMatter] = useState(null);
  const [events, setEvents] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = `/agent/matters/${encodeURIComponent(matterRef)}`;
        const [one, timeline] = await Promise.all([
          agentFetch(path, { token }),
          agentFetch(`${path}/events`, { token }),
        ]);
        if (cancelled) return;
        if (one.status === 401 || timeline.status === 401) { onSignedOut(); return; }
        if (!one.ok) { setState("missing"); return; }

        setMatter((await one.json()).matter);
        setEvents(timeline.ok ? (await timeline.json()).events || [] : []);
        setState("ready");
      } catch (err) {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [matterRef, token, onSignedOut]);

  if (state === "loading") return <Centred><Spinner /></Centred>;

  if (state !== "ready") {
    return (
      <div>
        <button className="ap-back" onClick={onBack}>← All matters</button>
        <Notice tone="error">
          {state === "missing"
            ? "We could not find that matter. It may have been closed."
            : "That did not load. Please try again in a moment."}
        </Notice>
      </div>
    );
  }

  const file = matter.file || {};

  return (
    <div>
      <button className="ap-back" onClick={onBack}>← All matters</button>

      <div className="ap-head">
        <h2 className="ap-h2">{matter.address || matter.ref}</h2>
        <Stage matter={matter} />
      </div>
      <p className="ap-sub">{matter.client ? `${matter.client} · ` : ""}Reference {matter.ref}</p>

      <section className="ap-card">
        <h3>Your contract</h3>
        <Contract matter={matter} token={token} onSignedOut={onSignedOut} />
      </section>

      <section className="ap-card">
        <h3>Where it has got to</h3>
        <dl className="ap-kv">
          <Fact label="Status" value={file.status} />
          {/* `stageKnown` is the middleware saying it recognised the office's
              own pipeline word. An unrecognised one is an internal label
              somebody typed, and it is withheld rather than forwarded. */}
          {file.stageKnown && <Fact label="Stage" value={file.stage} />}
          <Fact label="Looked after by" value={matter.fileHandlerName} />
          <Fact label="Opened" value={formatOpenedDate(matter.openedDate)} />
          <dt>Searches</dt>
          <dd>{matter.searchCount ? <Progress matter={matter} /> : <span className="ap-faint">Not ordered yet</span>}</dd>
        </dl>
      </section>

      <section className="ap-card">
        <h3>Activity</h3>
        <Timeline events={events} />
      </section>
    </div>
  );
}

// ── The shell ────────────────────────────────────────────────────────────────

// ── The other account this same person has ───────────────────────────────────
//
// **A handover, not a second sign-in.** The press asks the middleware for a
// link and follows it; what comes back is the reseller portal's own one-time
// sign-in URL, so nothing about that portal changes and the session it opens is
// its own — this one is untouched, and coming back is the same press the other
// way.
//
// **A navigation rather than a new tab**, the rule *Open the contract* already
// follows: the URL is only known after a round trip, and a `window.open` that
// far from the press is one a popup blocker stops — which reads as a button
// that does nothing.
//
// **And a failure is said out loud.** Being sent nowhere with no explanation is
// the one outcome worse than not offering the switch at all.
function SwitchToBrand({ token, brands, onProblem }) {
  const [busy, setBusy] = useState("");
  if (!brands.length) return null;

  const go = async (brand) => {
    setBusy(brand.resellerId);
    onProblem("");
    try {
      const res = await agentFetch("/agent/portal-switch", {
        token,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resellerId: brand.resellerId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) {
        setBusy("");
        onProblem(body.error || "Could not open that just now.");
        return;
      }
      window.location.assign(body.url);
    } catch (err) {
      setBusy("");
      onProblem("Could not reach us just now.");
    }
  };

  return (
    <>
      {brands.map(brand => (
        <button key={brand.resellerId} role="menuitem" disabled={Boolean(busy)}
          onClick={() => go(brand)}>
          {busy === brand.resellerId ? "Opening…" : `${brand.name} admin`}
        </button>
      ))}
      <div className="ap-menu-rule" />
    </>
  );
}

export function AvatarMenu({ agent, office, token, brands = [], onSignOut }) {
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState("");
  const box = useRef(null);

  // A menu that only closed on its own button is one that stays open behind
  // whatever you pressed next.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const escape = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="ap-who" ref={box}>
      <button className="ap-avatar" aria-haspopup="menu" aria-expanded={open}
        aria-label="Your account" onClick={() => setOpen(o => !o)}>
        {initialsOf(agent.name, agent.email)}
      </button>
      {open && (
        <div className="ap-menu" role="menu">
          <div className="ap-menu-who">
            <div style={{ fontSize: 13, fontWeight: 600 }}>{agent.name || agent.email}</div>
            <div style={{ fontSize: 12, color: T.textFaint, marginTop: 2 }}>{office || agent.email}</div>
          </div>
          <SwitchToBrand token={token} brands={brands} onProblem={setProblem} />
          {problem && <div className="ap-menu-problem">{problem}</div>}
          <button role="menuitem" onClick={onSignOut}>Sign out</button>
        </div>
      )}
    </div>
  );
}

// ── Ordering a section 32 ────────────────────────────────────────────────────
//
// **The one thing on this screen an agent does rather than reads.** Everything
// else here is a view of work the office is already doing; this starts some.
//
// What the press actually does is two emails and no matter: the vendor is sent
// the client form, and the office is sent the referral so it can raise the
// quote and open the file. A matter appears in **Matters** when the client's
// own form comes back — which is why this view says so rather than leaving
// somebody watching a list that will not move for a day.
//
// **The form states no rules of its own.** Which fields are needed, what an
// address has to look like and what happens to a second press are all the
// middleware's, and a copy here would drift — the way it would drift is a form
// that accepts what the route refuses, which reads as a broken button. `required`
// and `type="email"` are the browser's own marks rather than a second rule, and
// everything else comes back as a sentence this renders as given.
export function OrderSection32({ token, onSignedOut }) {
  const blank = { address: "", vendorName: "", vendorEmail: "", vendorPhone: "", note: "" };
  const [form, setForm] = useState(blank);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [placed, setPlaced] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  // What has already been asked for. Fetched here rather than in the root
  // because it is only ever wanted on this view — the matters list is fetched
  // there because two views read it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await agentFetch("/agent/referrals", { token });
        if (cancelled) return;
        if (res.status === 401) { onSignedOut(); return; }
        if (res.ok) setPlaced((await res.json()).referrals || []);
      } catch (err) { /* the form is the point of the screen; a list that did not load is not */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [token, onSignedOut]);

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await agentFetch("/agent/referrals", {
        method: "POST",
        token,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 401) { onSignedOut(); return; }
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        // The service's own sentence where it has one: a 400 is about what was
        // typed and a 503 is about us, and only one of those has a wording this
        // file could improve on.
        setError(body?.error || "The referral could not be sent. Please try again in a moment.");
        setState("idle");
        return;
      }
      setSent(body.referral || null);
      // Prepended from the answer rather than re-fetched: it is the server's
      // own view of the row, so the list cannot disagree with the confirmation
      // above it, and a second request would be a round trip for something
      // already in hand.
      setPlaced(rows => [body.referral, ...rows.filter(r => r.id !== body.referral?.id)]);
      setForm(blank);
      setState("idle");
    } catch (err) {
      setError("Could not reach us just now. Please check your connection and try again.");
      setState("idle");
    }
  };

  if (sent) {
    return (
      <>
        <h2 className="ap-h2">Section 32 ordered</h2>
        <p className="ap-sub">{sent.address}</p>

        <section className="ap-card">
          {/* **What actually went, in the service's own words.** A screen that
              said the vendor had been emailed when nothing did is the one claim
              this must not make, so the sentence comes down with the row. */}
          <p className="ap-lede" style={{ color: T.text }}>
            {sent.clientEmailed
              ? `We have emailed ${sent.vendorName} the form to complete, and told them their quote will follow.`
              : sent.clientNote || "The office has been told."}
          </p>
          <p className="ap-lede" style={{ marginTop: 12 }}>
            Our office has the referral and will raise the quote. The matter appears
            in <strong style={{ color: T.text }}>Matters</strong> once {sent.vendorName.split(" ")[0]} has
            filled in the form.
          </p>
          <div className="ap-actions" style={{ marginTop: 18 }}>
            <Btn variant="raised" onClick={() => setSent(null)}>Order another</Btn>
          </div>
        </section>

        <PlacedList placed={placed} loaded />
      </>
    );
  }

  return (
    <>
      <h2 className="ap-h2">Order a section 32</h2>
      <p className="ap-sub">
        Tell us about the property and the vendor. We will email them the form we need
        filled in, and our office will send their quote and open the file.
      </p>

      {error && <div style={{ marginBottom: 18 }}><Notice tone="error">{error}</Notice></div>}

      <form onSubmit={submit} className="ap-card" style={{ maxWidth: 720 }}>
        <label htmlFor="ref-address" className="ap-label">Property address</label>
        <input id="ref-address" className="ap-input" required value={form.address}
          onChange={set("address")} placeholder="12 Smith Street, Richmond" />

        <div className="ap-fields">
          <div>
            <label htmlFor="ref-name" className="ap-label">Vendor’s name</label>
            <input id="ref-name" className="ap-input" required value={form.vendorName}
              onChange={set("vendorName")} placeholder="Mary Jones" />
          </div>
          <div>
            <label htmlFor="ref-email" className="ap-label">Vendor’s email</label>
            <input id="ref-email" className="ap-input" required type="email" value={form.vendorEmail}
              onChange={set("vendorEmail")} placeholder="mary@example.com" />
          </div>
          <div>
            <label htmlFor="ref-phone" className="ap-label">Vendor’s phone (optional)</label>
            <input id="ref-phone" className="ap-input" value={form.vendorPhone}
              onChange={set("vendorPhone")} placeholder="0400 000 000" />
          </div>
        </div>

        <label htmlFor="ref-note" className="ap-label">Anything we should know (optional)</label>
        <textarea id="ref-note" className="ap-input ap-textarea" rows={3} value={form.note}
          onChange={set("note")}
          placeholder="Vendor is overseas until the 20th — best reached by email." />
        {/* The note goes to the office in the agent’s own words, so it is worth
            saying that it is read by a person rather than filed. */}
        <p className="ap-hint">Goes straight to the conveyancer who picks up the file.</p>

        <Btn type="submit" disabled={state === "sending"} className="ap-wide">
          {state === "sending" ? "Sending…" : "Send the vendor their form"}
        </Btn>
      </form>

      <PlacedList placed={placed} loaded={loaded} />
    </>
  );
}

// What this agent has already asked for.
//
// **A press that answers and says nothing reads as a broken button**, and the
// matter it produces does not exist for a day — so without this an agent
// presses, gets a confirmation, comes back tomorrow and finds no trace of it
// anywhere on the screen.
//
// **No status beyond what was sent.** A referral has no lifecycle here: nothing
// links it to the matter that eventually opens, because the only thing joining
// them is the property address and matching addresses is precisely what the
// middleware is careful about — four sources write one address four ways, and a
// wrong link would put somebody else's matter on this screen.
function PlacedList({ placed, loaded }) {
  if (!loaded || !placed.length) return null;
  return (
    <section style={{ marginTop: 28 }}>
      <h3 className="ap-h3">Already ordered</h3>
      <ul className="ap-placed">
        {placed.map(row => (
          <li key={row.id}>
            <div className="ap-propcell">
              <div className="ap-prop">{row.address}</div>
              <div className="ap-ref">
                {[row.vendorName, row.agentName].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="ap-cell">
              {row.clientEmailed ? "Form sent to the vendor" : row.clientNote || "With the office"}
            </div>
            <time className="ap-cell">{eventDate(row.createdAt)}</time>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────

export default function AgentPortal() {
  const [session, setSession] = useState(() => ({
    token: readStoredSession(), agent: {}, office: "", state: "checking",
  }));
  const [matters, setMatters] = useState([]);
  const [openRef, setOpenRef] = useState(() =>
    (typeof window === "undefined" ? null : readAgentLocation(window.location.search).ref));
  const [view, setView] = useState(() =>
    (typeof window === "undefined" ? "matters" : readAgentLocation(window.location.search).view));
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  // Every move through the portal goes through here, so the address bar and the
  // screen cannot disagree. Back and Forward come in through `popstate`.
  const go = useCallback((next, how = "push") => {
    setView(next.view);
    setOpenRef(next.ref || null);
    // A press on the place you already are adds nothing to Back.
    const same = typeof window !== "undefined" && window.location.search === agentLocationSearch(next);
    writeAgentLocation(next, same ? "replace" : how);
  }, []);
  useEffect(() => {
    const onPop = () => {
      const at = readAgentLocation(window.location.search);
      setView(at.view);
      setOpenRef(at.ref);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // **The rail folds, and the choice is remembered.** An agent working one
  // matter at a time wants the document wide; one moving between them wants the
  // labels. Neither is the right default for everybody, so it is theirs to set
  // and it has to survive a reload — a control that resets on every visit is
  // one people stop touching.
  //
  // `localStorage` is right here where `App.js` says it is a trap: that warning
  // is about the **staff portal running in a third-party iframe**, where the
  // browser partitions storage and the frame reads a different bucket from a
  // top-level tab. This is a top-level page on its own origin, so there is no
  // partitioning to be caught by — and the value is a per-viewer convenience
  // rather than anything the service needs to know.
  //
  // Wrapped either way: a private window or blocked site data makes the
  // accessor throw, and a preference is never worth a blank screen.
  const [railOpen, setRailOpen] = useState(() => {
    try { return window.localStorage.getItem(RAIL_KEY) !== "mini"; } catch (err) { return true; }
  });

  const foldRail = () => {
    setRailOpen(open => {
      const next = !open;
      try { window.localStorage.setItem(RAIL_KEY, next ? "full" : "mini"); } catch (err) { /* a preference is not worth a throw */ }
      return next;
    });
  };

  const signOutLocally = useCallback((message = "") => {
    writeStoredSession("");
    setSession({ token: "", agent: {}, office: "", state: "signedOut" });
    setMatters([]);
    setOpenRef(null);
    writeAgentLocation({ view: "matters", ref: null }, "replace");
    setError(message);
  }, []);

  // Redeem an emailed link, then find out who we are. One effect, because the
  // second depends on the first — and a separate "am I signed in?" pass would
  // race the redemption and briefly show the sign-in form to somebody who had
  // just clicked their link.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      let token = readStoredSession();

      if (LINK_TOKEN) {
        // Shared across however many times this effect runs — StrictMode runs
        // it twice in development, and the token is single-use.
        redeeming = redeeming || agentFetch("/agent/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: LINK_TOKEN }),
        }).then(async (res) => ({ status: res.status, body: await res.json().catch(() => null) }));

        try {
          const { status, body } = await redeeming;
          if (cancelled) return;
          if (status === 200) {
            // Absent in cookie mode, which is the point of checking rather than
            // assuming: the session is then carried by the HttpOnly cookie and
            // there is nothing for this app to hold.
            token = body?.sessionToken || "";
            writeStoredSession(token);
          } else {
            signOutLocally(signInError(status, body));
            return;
          }
        } catch (err) {
          if (!cancelled) signOutLocally("Could not reach us just now. Please try your link again.");
          return;
        }
      }

      try {
        const res = await agentFetch("/agent/me", { token });
        if (cancelled) return;
        if (!res.ok) { signOutLocally(); return; }
        const body = await res.json();
        setSession({
          token,
          agent: body.agent || {},
          office: body.office?.name || "",
          // Read off the answer rather than inferred from the rows: a principal
          // whose agency happens to have one live matter is not a principal
          // with one matter, and a heading worked out from the list would say
          // so on exactly the quiet week.
          officeAdmin: body.officeAdmin === true,
          // Somewhere else this same person can sign in. An empty list is the
          // ordinary answer and a missing key is an older middleware, and both
          // read the same way: no switch to offer.
          brands: Array.isArray(body.brands) ? body.brands : [],
          state: "signedIn",
        });
      } catch (err) {
        if (!cancelled) setSession(s => ({ ...s, state: "error" }));
      }
    })();

    return () => { cancelled = true; };
  }, [signOutLocally]);

  // Their matters. Re-fetched when the list is returned to, so a contract
  // released while somebody was reading a matter shows up without a reload.
  useEffect(() => {
    if (session.state !== "signedIn" || openRef) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await agentFetch("/agent/matters", { token: session.token });
        if (cancelled) return;
        if (res.status === 401) { signOutLocally(); return; }
        if (!res.ok) { setError("Your matters did not load. Please try again in a moment."); return; }
        setMatters((await res.json()).matters || []);
        setError("");
      } catch (err) {
        if (!cancelled) setError("Could not reach us just now. Please check your connection.");
      }
    })();

    return () => { cancelled = true; };
  }, [session.state, session.token, openRef, signOutLocally]);

  const signOut = async () => {
    try {
      await agentFetch("/agent/sign-out", { method: "POST", token: session.token });
    } catch (err) { /* the session is going either way */ }
    signOutLocally();
  };

  // **Contracts is a filter, not a second dataset.** It is the same rows with
  // the same scope, narrowed by `inContracts` — see the rule beside it, which
  // is what the two views mean rather than an implementation detail of this
  // line. There is no second request, so there is no second place for the
  // scoping to be got wrong.
  const ready = matters.filter(inContracts);
  const inView = view === "contracts" ? ready : matters;
  const searching = matters.length > MATTER_SEARCH_MIN;
  const q = searching ? query.trim() : "";
  const shown = q ? inView.filter(m => matterMatches(m, q)) : inView;
  const signedIn = session.state === "signedIn";

  const open = (ref) => { go({ view, ref }); };
  // Back to the list. When the matter was opened from the list in this tab,
  // that is the browser's own Back, so the history does not grow a loop of
  // list → matter → list; arriving on a pasted link, there is nothing behind
  // it to go back to, so the list replaces the matter in place.
  const openedHere = useRef(false);
  useEffect(() => { if (!openRef) openedHere.current = false; }, [openRef]);
  const backToList = () => {
    if (openedHere.current) window.history.back();
    else go({ view, ref: null }, "replace");
  };
  const openFromList = (ref) => { openedHere.current = true; open(ref); };

  // `/` finds, from anywhere on the list that is not already a text box.
  useEffect(() => {
    if (!signedIn || openRef || !searching || view === "order") return undefined;
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [signedIn, openRef, searching, view]);

  return (
    <div className="ap-root">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" />

      <header className="ap-bar">
        <div className="ap-logo">
          {/* The firm's own wordmark rather than the word typed out. It is the
              same file the contract's cover carries, so an agent who has the
              PDF open beside this is looking at one mark rather than two. */}
          <img src={LOGO_SRC} alt="Provey" />
          <span className="ap-eyebrow">Agent portal</span>
        </div>
        {signedIn && (
          <AvatarMenu agent={session.agent} office={session.office}
            token={session.token} brands={session.brands || []}
            onSignOut={signOut} />
        )}
      </header>

      {!signedIn ? (
        <main className="ap-main">
          {session.state === "checking" && <Centred><Spinner /></Centred>}
          {session.state === "error" && (
            <Notice tone="error">We could not reach the service. Please refresh in a moment.</Notice>
          )}
          {session.state === "signedOut" && <SignIn initialError={error} />}
        </main>
      ) : (
        <div className="ap-shell" data-rail={railOpen ? "full" : "mini"}>
          <nav className="ap-side" aria-label="Sections">
            <div className="ap-side-group">Your matters</div>
            <div className="ap-nav">
              {/* **The label carries `title` and an explicit `aria-label`**, so
                  a folded rail is still readable to a hover and a screen
                  reader. The count is hidden rather than shrunk: a bare number
                  under an icon reads as a badge nobody can explain, and the
                  view itself says how many the moment you open it. */}
              <button aria-current={view === "matters" ? "page" : undefined}
                aria-label="Matters" title="Matters"
                onClick={() => go({ view: "matters", ref: null })}>
                <IconMatters />
                <span className="ap-nav-label">Matters</span>
                <span className="ap-count">{matters.length}</span>
              </button>
              <button aria-current={view === "contracts" ? "page" : undefined}
                aria-label="Contracts" title="Contracts"
                onClick={() => go({ view: "contracts", ref: null })}>
                <IconContracts />
                <span className="ap-nav-label">Contracts</span>
                <span className="ap-count">{ready.length}</span>
              </button>
            </div>

            {/* **Its own group, below the two views rather than above them.**
                An agent opens this portal to watch matters and orders one
                occasionally, so the thing they do daily reads first — and a
                third button under *Your matters* would be filed under a
                heading it is not a view of.

                **No count.** The other two say how many are waiting on you;
                how many referrals you have placed is a different kind of
                number, and a bare one under an icon reads as a badge nobody
                can explain. The view says how many the moment you open it. */}
            <div className="ap-side-group">New</div>
            <div className="ap-nav">
              <button aria-current={view === "order" ? "page" : undefined}
                aria-label="Order a section 32" title="Order a section 32"
                onClick={() => go({ view: "order", ref: null })}>
                <IconOrder />
                <span className="ap-nav-label">Order a section 32</span>
              </button>
            </div>

            {/* Below the breakpoint the rail is already a horizontal strip, so
                there is nothing to fold and the control is hidden rather than
                offered as a press that does nothing visible. */}
            <button className="ap-fold" onClick={foldRail}
              aria-expanded={railOpen}
              aria-label={railOpen ? "Collapse the menu" : "Expand the menu"}
              title={railOpen ? "Collapse the menu" : "Expand the menu"}>
              <IconFold open={railOpen} />
              <span className="ap-nav-label">Collapse</span>
            </button>
          </nav>

          <main className="ap-main">
            {openRef ? (
              <MatterView matterRef={openRef} token={session.token}
                onBack={backToList} onSignedOut={() => signOutLocally()} />
            ) : view === "order" ? (
              <OrderSection32 token={session.token} onSignedOut={() => signOutLocally()} />
            ) : (
              <>
                <h2 className="ap-h2">{view === "contracts" ? "Contracts" : "Matters"}</h2>
                <p className="ap-sub">
                  {view === "contracts"
                    ? "Sales we have issued a contract for, and that have not reached a settlement date. Open one to read it, or download it as a single PDF."
                    : session.officeAdmin
                      ? "Every matter this agency is acting on with us — purchase or sale, and they stay here after exchange and settlement."
                      : "Every matter you are acting on with us — purchase or sale, and they stay here after exchange and settlement."}
                </p>

                {error && <div style={{ marginBottom: 18 }}><Notice tone="error">{error}</Notice></div>}

                {/* Only once there are enough to need it: on a short list the
                    box is one more thing to read for nothing. Enter opens the
                    top row; Esc empties the box. */}
                {searching && (
                  <input ref={searchRef} type="search" className="ap-search"
                    aria-label="Find a matter" placeholder="Find by address, vendor or ref   /"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { setQuery(""); e.currentTarget.blur(); }
                      if (e.key === "Enter" && shown.length) openFromList(shown[0].ref);
                    }} />
                )}

                <MatterList
                  matters={shown}
                  onOpen={openFromList}
                  empty={q
                    ? {
                      title: `Nothing matches “${q}”`,
                      body: view === "contracts"
                        ? "No contract waiting has that address, vendor or ref. It may be under Matters — or press Esc to clear the search."
                        : "No matter has that address, vendor or ref. Press Esc to clear the search.",
                    }
                    : view === "contracts"
                    ? {
                      title: "No contracts waiting",
                      body: "A sale appears here once we have issued its contract, and leaves once it has a settlement date. We will email you when one is ready.",
                    }
                    : undefined}
                />
              </>
            )}
          </main>
        </div>
      )}

      <style>{SHEET}</style>
    </div>
  );
}

// The layout, in one place. Every colour is interpolated from `shared.js`, so
// there is still exactly one palette — what is here is the shape, which is the
// part that cannot be written inline: hover, `aria-current`, and the phone.
const SHEET = `
* { box-sizing: border-box; }
.ap-root { min-height: 100vh; background: ${T.bg}; color: ${T.text}; font-family: Inter, sans-serif; -webkit-font-smoothing: antialiased; }
button:focus-visible, a:focus-visible { outline: 2px solid ${T.brown}; outline-offset: 2px; }
@keyframes s32-spin { to { transform: rotate(360deg); } }

.ap-bar { height: 56px; background: ${T.white}; border-bottom: 1px solid ${T.border}; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 0 18px; }
.ap-logo { display: flex; align-items: center; gap: 11px; min-width: 0; }
.ap-logo img { height: 19px; width: auto; display: block; }
.ap-eyebrow { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: ${T.textFaint}; white-space: nowrap; }

.ap-who { position: relative; flex: 0 0 auto; }
.ap-avatar { width: 32px; height: 32px; border-radius: 50%; background: ${T.bgAlt}; border: 1px solid ${T.borderMid}; font-family: inherit; font-size: 11.5px; font-weight: 600; color: ${T.text}; cursor: pointer; }
.ap-menu { position: absolute; right: 0; top: 40px; min-width: 200px; background: ${T.white}; border: 1px solid ${T.border}; border-radius: 10px; box-shadow: 0 8px 28px rgba(0,0,0,0.10); padding: 6px; z-index: 20; }
.ap-menu-who { padding: 8px 10px 10px; border-bottom: 1px solid ${T.border}; margin-bottom: 6px; }
.ap-menu button { display: block; width: 100%; text-align: left; font-family: inherit; font-size: 13px; color: ${T.text}; background: none; border: 0; border-radius: 7px; padding: 8px 10px; cursor: pointer; }
.ap-menu button:hover { background: ${T.bg}; }
.ap-menu button[disabled] { color: ${T.textFaint}; cursor: default; }
.ap-menu-rule { height: 1px; background: ${T.border}; margin: 6px 0; }
.ap-menu-problem { padding: 4px 10px 8px; font-size: 12px; color: ${T.red}; }

.ap-shell { display: grid; grid-template-columns: 208px minmax(0, 1fr); min-height: calc(100vh - 56px); }
.ap-side { background: ${T.white}; border-right: 1px solid ${T.border}; padding: 16px 10px; }
.ap-side-group { font-size: 10.5px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: ${T.textFaint}; padding: 0 10px; margin-bottom: 8px; }
/* The second group and everything after it. A run of buttons with a
   heading dropped into the middle of it reads as one list somebody
   interrupted; the space is what makes it two. */
.ap-side-group + .ap-nav { margin-bottom: 0; }
.ap-nav + .ap-side-group { margin-top: 20px; }
.ap-nav { display: flex; flex-direction: column; gap: 2px; }
.ap-nav button { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; font-family: inherit; font-size: 13.5px; color: ${T.text}; background: none; border: 0; border-radius: 8px; padding: 8px 10px; cursor: pointer; }
.ap-nav button:hover { background: ${T.bg}; }
.ap-nav button[aria-current="page"] { background: ${T.bgAlt}; font-weight: 500; }
.ap-nav button svg { flex: 0 0 auto; color: ${T.textFaint}; }
.ap-nav button[aria-current="page"] svg { color: ${T.text}; }
.ap-count { margin-left: auto; font-size: 12px; color: ${T.textFaint}; font-variant-numeric: tabular-nums; }

/* The fold, and the folded rail.
   Driven off one attribute on the shell rather than a class per element, so
   there is a single place the two states are described and no way for the
   column width and the labels to disagree about which one is showing. */
.ap-fold { display: flex; align-items: center; gap: 10px; width: 100%; margin-top: 10px; padding: 8px 10px; font-family: inherit; font-size: 12.5px; color: ${T.textFaint}; background: none; border: 0; border-top: 1px solid ${T.border}; border-radius: 0; cursor: pointer; }
.ap-fold:hover { color: ${T.text}; }
.ap-fold svg { flex: 0 0 auto; }

.ap-shell[data-rail="mini"] { grid-template-columns: 57px minmax(0, 1fr); }
.ap-shell[data-rail="mini"] .ap-side { padding: 16px 8px; }
.ap-shell[data-rail="mini"] .ap-side-group { display: none; }
.ap-shell[data-rail="mini"] .ap-nav-label,
.ap-shell[data-rail="mini"] .ap-count { display: none; }
.ap-shell[data-rail="mini"] .ap-nav button,
.ap-shell[data-rail="mini"] .ap-fold { justify-content: center; padding-left: 0; padding-right: 0; }

.ap-main { padding: 24px 26px 34px; min-width: 0; }
.ap-centred { display: flex; align-items: center; justify-content: center; padding: 84px 20px; }
.ap-h1, .ap-h2 { font-weight: 300; letter-spacing: -0.02em; color: ${T.text}; margin: 0; }
.ap-h1 { font-size: 24px; }
.ap-h2 { font-size: 23px; }
.ap-search { width: 100%; max-width: 420px; padding: 10px 14px; font-size: 14px; font-family: inherit; color: ${T.text}; background: ${T.white}; border: 1px solid ${T.borderMid}; border-radius: 999px; margin: 0 0 16px; }
.ap-sub { font-size: 13.5px; color: ${T.textSoft}; margin: 6px 0 20px; }
.ap-lede { font-size: 13.5px; color: ${T.textSoft}; line-height: 1.7; margin: 0; }
.ap-faint { font-size: 13px; color: ${T.textFaint}; }
.ap-head { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.ap-back { background: none; border: 0; padding: 0; margin-bottom: 18px; font-family: inherit; font-size: 13px; color: ${T.textSoft}; cursor: pointer; }
.ap-back:hover { color: ${T.text}; }

.ap-label { display: block; font-size: 10px; font-weight: 600; color: ${T.textFaint}; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
.ap-input { width: 100%; padding: 12px 14px; font-size: 14px; font-family: inherit; color: ${T.text}; background: ${T.white}; border: 1px solid ${T.borderMid}; border-radius: 10px; margin-bottom: 16px; }

.ap-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit; font-size: 13px; font-weight: 500; padding: 8px 16px; border-radius: 999px; border: 1px solid transparent; cursor: pointer; transition: opacity 0.15s; }
.ap-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.ap-btn.primary { background: ${T.brown}; color: ${T.white}; border-color: ${T.brown}; }
.ap-btn.raised { background: ${T.white}; color: ${T.text}; border-color: ${T.borderMid}; }
.ap-btn.ghost { background: transparent; color: ${T.textSoft}; border-color: ${T.border}; }
.ap-btn.ap-wide { width: 100%; }
.ap-actions { display: flex; flex-wrap: wrap; gap: 10px; }

.ap-note { background: ${T.white}; border: 1px solid ${T.border}; border-left: 2px solid ${T.borderMid}; border-radius: 8px; padding: 12px 14px; font-size: 13px; line-height: 1.6; color: ${T.text}; }
.ap-note.ap-alert { border-color: ${T.borderMid}; border-left-color: ${T.red}; }

.ap-listhead, .ap-row { display: grid; grid-template-columns: minmax(0, 1fr) 150px 96px 124px 228px; gap: 16px; align-items: center; padding: 0 14px; }
.ap-listhead { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: ${T.textFaint}; padding-bottom: 8px; }
.ap-row { width: 100%; text-align: left; font-family: inherit; background: ${T.white}; border: 1px solid ${T.border}; border-radius: 10px; padding: 13px 14px; margin-bottom: 8px; cursor: pointer; }
.ap-row:hover { background: ${T.bgAlt}; border-color: ${T.borderMid}; }
/* **The address truncates; it used to vanish.** text-overflow: ellipsis does
   nothing without nowrap — so this wrapped instead, and in a track squeezed
   towards zero it wrapped to one character a line and was clipped away
   entirely, leaving a very tall row with the reference sitting under nothing.
   A min-width of zero on the cell is the other half: nowrap makes the
   min-content width the whole address, which without it would push the item
   out of its own column the way the stage pill did.
   (No backticks in here: this is inside a template literal.) */
.ap-propcell { min-width: 0; }
.ap-prop { font-size: 13.5px; font-weight: 500; color: ${T.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ap-ref { font-size: 12px; color: ${T.textFaint}; font-variant-numeric: tabular-nums; margin-top: 2px; }
.ap-cell { font-size: 13px; color: ${T.textSoft}; min-width: 0; overflow: hidden; text-overflow: ellipsis; }

/* **The label is the middleware's, so the column has to survive one this file
   has never seen.** The pill was set nowrap in a track narrower than the
   sentence it holds, and the cell had no min-width of zero — so the item
   overflowed its own column and the border ran off the right edge of the card.
   Two halves: the column is wide enough for the longest label the service
   sends today, and a longer one wraps inside the pill rather than escaping it.
   A wrapped pill reads as a long label; a clipped one reads as a broken
   screen. */
.ap-stage { min-width: 0; }
.ap-pill { display: inline-flex; align-items: center; gap: 7px; border: 1px solid currentColor; border-radius: 999px; padding: 2px 11px; font-size: 12px; font-weight: 500; line-height: 1.6; max-width: 100%; }
.ap-pill::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex: 0 0 auto; }

.ap-card { background: ${T.white}; border: 1px solid ${T.border}; border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; }
.ap-card h3 { font-size: 15px; font-weight: 600; color: ${T.text}; margin: 0 0 14px; }
.ap-h3 { font-size: 13px; font-weight: 600; color: ${T.text}; margin: 0 0 12px; }

/* ── Ordering a section 32 ─────────────────────────────────────────────────
   The three short fields sit side by side where there is room, because a
   column of one-line boxes each the width of the card reads as a longer form
   than it is. The address and the note stay full width: one is long and the
   other is a paragraph. */
.ap-fields { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 14px; }
.ap-textarea { resize: vertical; min-height: 76px; line-height: 1.6; margin-bottom: 6px; }
.ap-hint { font-size: 12px; color: ${T.textFaint}; margin: 0 0 18px; }

/* Deliberately not .ap-row: a referral is not a matter and does not open.
   Same ink and the same radius, no hover and no pointer — a row that lights up
   under the cursor and then does nothing is the worst kind of affordance. */
.ap-placed { list-style: none; margin: 0; padding: 0; }
.ap-placed li { display: grid; grid-template-columns: minmax(0, 1fr) 260px 110px; gap: 16px; align-items: center; background: ${T.white}; border: 1px solid ${T.border}; border-radius: 10px; padding: 13px 14px; margin-bottom: 8px; }
.ap-placed time { font-variant-numeric: tabular-nums; }

.ap-kv { display: grid; grid-template-columns: 152px minmax(0, 1fr); gap: 10px 16px; margin: 0; font-size: 13px; align-items: baseline; }
.ap-kv dt { color: ${T.textFaint}; }
.ap-kv dd { margin: 0; color: ${T.text}; min-width: 0; }
.ap-kv dd.blank { color: ${T.textFaint}; font-style: italic; }

.ap-feed { list-style: none; margin: 0; padding: 0; }
.ap-feed li { display: grid; grid-template-columns: 96px minmax(0, 1fr); gap: 14px; padding: 10px 0; border-bottom: 1px solid ${T.border}; font-size: 13.5px; line-height: 1.5; color: ${T.text}; }
.ap-feed li:last-child { border-bottom: 0; padding-bottom: 0; }
.ap-feed time { font-size: 12.5px; color: ${T.textFaint}; font-variant-numeric: tabular-nums; }

/* ── The table stops being a table before the rail stops being a rail ──────
   Five columns need 692px before the address gets any room at all — 598 of
   fixed track, 64 of gap, 28 of the row's own padding and its border — and the
   rail and the page gutters take a further 260 out of the viewport. So the
   window has to be about 1130px wide before Property has 180 to be read in,
   and under that the columns collapse: the track goes to zero, the fixed ones
   overflow the card, and the header's own words print on top of each other.
   That is what an iPad in portrait, or a phone held sideways, was getting.

   **Sized for the rail unfolded**, which is the safe direction: folding only
   ever gives the table more room, and a media query cannot see the fold.

   It is a separate breakpoint from the phone one below deliberately — the rail
   is perfectly good as a sidebar at 900px, and one query doing both jobs is
   what tied the table's minimum to a number chosen for the rail. */
@media (max-width: 1130px) {
  .ap-listhead { display: none; }
  .ap-row { grid-template-columns: minmax(0, 1fr); gap: 8px; }
  .ap-cell[data-label]::before { content: attr(data-label) ": "; color: ${T.textFaint}; }
  /* A card is as wide as the page, so the whole address fits over two lines —
     truncating it here would hide half of what names the matter. */
  .ap-prop { white-space: normal; }
}

@media (max-width: 760px) {
  .ap-shell { grid-template-columns: minmax(0, 1fr); }
  .ap-side { border-right: 0; border-bottom: 1px solid ${T.border}; padding: 10px; }
  .ap-side-group { display: none; }
  /* **One strip, not two.** The rail has two groups now, and with the
     headings hidden they would stack as two rows of buttons — which reads as
     a menu that wrapped rather than as two kinds of thing. The column itself
     becomes the row, so every button scrolls together. */
  .ap-side { display: flex; flex-direction: row; gap: 6px; overflow-x: auto; }
  .ap-nav { flex-direction: row; gap: 6px; }
  .ap-nav button { width: auto; flex: 0 0 auto; white-space: nowrap; }
  .ap-nav + .ap-side-group { margin-top: 0; }

  /* Already a strip: there is no column to fold, so the control would be a
     press with nothing to show for it. The mini rules go with it, or a
     viewer who folded the rail on a desktop would find the labels gone on
     their phone with no way to bring them back. */
  .ap-fold { display: none; }
  .ap-shell[data-rail="mini"] { grid-template-columns: minmax(0, 1fr); }
  .ap-shell[data-rail="mini"] .ap-side { padding: 10px; }
  .ap-shell[data-rail="mini"] .ap-nav-label { display: inline; }
  .ap-shell[data-rail="mini"] .ap-count { display: inline; }
  .ap-shell[data-rail="mini"] .ap-nav button { justify-content: flex-start; padding-left: 10px; padding-right: 10px; }
  /* Three boxes side by side at phone width are three boxes too narrow to
     read what is typed into them, and a referral row's three columns are the
     same fault one screen along. */
  .ap-fields { grid-template-columns: minmax(0, 1fr); }
  .ap-placed li { grid-template-columns: minmax(0, 1fr); gap: 6px; }

  .ap-main { padding: 20px 16px 32px; }
  .ap-kv { grid-template-columns: minmax(0, 1fr); gap: 2px; }
  .ap-kv dd { margin-bottom: 10px; }
  .ap-feed li { grid-template-columns: minmax(0, 1fr); gap: 3px; }
}
`;
